import { api } from "../lib";
import type { PaginatedResponse } from "./cases.service";

export interface BillingStats {
  casesReceivedThisMonth: number;
  awaitingBilling: {
    total: number;
    pending: number;
    error: number;
    rejected: number;
  };
  billedThisMonth: number;
  apiSubmittedThisMonth?: number;
  manualEnteredThisMonth?: number;
  unattributedSubmittedThisMonth?: number;
  realizedRevenue: number;
  apiSubmittedChargeValue?: number;
  manualEnteredChargeValue?: number;
  unattributedSubmittedChargeValue?: number;
  pipelineRevenue: number;
  feesConfigured: boolean;
  monthRange: { label: string; year: number; monthIndex: number };
}

export interface BillingCase {
  id: string;
  caseNumber: string;
  patientName: string | null;
  physicianName: string | null;
  insuranceProvider: string | null;
  billingScenario: string | null;
  signOffDate: string;
  status:
    | "Ready"
    | "Submitting"
    | "Submitted"
    | "Billed"
    | "Error"
    | "Rejected"
    | "Reconciliation Required";
  billingStatus:
    | "pending"
    | "submitting"
    | "submitted"
    | "billed"
    | "error"
    | "rejected"
    | "reconciliation_required";
  billedAt: string | null;
  billingSubmittedAt: string | null;
  tebraEncounterId: string | null;
  billingSubmissionMethod?: "manual" | "api" | null;
  billingErrorMessage: string | null;
  billingRejectionReason: string | null;
}

export interface BillingSubmitResponse {
  success: boolean;
  caseId: string;
  tebraEncounterId?: string;
  tebraPatientId?: string;
  message: string;
  billingStatus?: string;
}

export interface BillingCasesParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface BillingStatsParams {
  year?: number;
  // 1-indexed (Jan = 1)
  month?: number;
}

export interface BillingConfiguration {
  automationEnabled: boolean;
  mode: "manual" | "api" | "blocked";
  message: string;
}

export interface MarkManualEntryRequest {
  tebraEncounterId: string;
  expectedReportVersion: number;
  packetHash: string;
  tebraPatientId?: string;
  tebraPatientCaseId?: string;
  note?: string;
}

export interface MarkManualEntryResponse {
  caseId: string;
  billingStatus: "submitted";
  billingSubmissionMethod: "manual";
  billingSubmittedAt: string;
  billingSubmittedReportVersion: number;
  tebraEncounterId: string;
  tebraPatientId: string | null;
  tebraPatientCaseId: string | null;
  message?: string;
}

export const billingService = {
  getConfiguration: async (): Promise<BillingConfiguration> =>
    api.get<BillingConfiguration>("/billing/configuration"),

  getStats: async (params: BillingStatsParams = {}): Promise<BillingStats> => {
    const searchParams = new URLSearchParams();
    if (params.year !== undefined) {
      searchParams.set("year", params.year.toString());
    }
    if (params.month !== undefined) {
      searchParams.set("month", params.month.toString());
    }
    const qs = searchParams.toString();
    return api.get<BillingStats>(`/billing/stats${qs ? `?${qs}` : ""}`);
  },

  getCasesReadyForBilling: async (
    params: BillingCasesParams = {}
  ): Promise<PaginatedResponse<BillingCase>> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set("page", params.page.toString());
    if (params.limit) searchParams.set("limit", params.limit.toString());
    if (params.status) searchParams.set("status", params.status);
    if (params.search) searchParams.set("search", params.search);
    if (params.dateFrom) searchParams.set("dateFrom", params.dateFrom);
    if (params.dateTo) searchParams.set("dateTo", params.dateTo);
    return api.get<PaginatedResponse<BillingCase>>(
      `/billing/cases?${searchParams.toString()}`
    );
  },

  submitToTebra: async (caseId: string): Promise<BillingSubmitResponse> => {
    return api.post<BillingSubmitResponse>(
      `/billing/cases/${caseId}/submit`,
      {}
    );
  },

  retrySubmission: async (caseId: string): Promise<BillingSubmitResponse> => {
    return api.post<BillingSubmitResponse>(
      `/billing/cases/${caseId}/retry`,
      {}
    );
  },

  markManuallyEntered: async (
    caseId: string,
    data: MarkManualEntryRequest
  ): Promise<MarkManualEntryResponse> =>
    api.post<MarkManualEntryResponse>(
      `/billing/cases/${caseId}/manual-entry`,
      data
    ),

  updateCaseCpt: async (
    caseId: string,
    data: {
      caseDiagnosisId: string;
      newCptCodeId: string;
      reason: string;
      action?: "apply" | "request";
    }
  ): Promise<{
    success: boolean;
    message: string;
    correctionRequestId?: string;
  }> => {
    return api.patch(`/billing/cases/${caseId}/cpt`, data);
  },
};
